<template>
  <div class="page-container app-page">
    <!-- 搜索栏 -->
    <div class="search-section">
      <el-input
        v-model="searchParams.keyword"
        placeholder="搜索商品名称或描述"
        clearable
        @keyup.enter="fetchProducts"
        class="search-input"
      >
        <template #prefix>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        </template>
        <template #append>
          <el-button type="primary" @click="fetchProducts">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </el-button>
        </template>
      </el-input>
      <el-select
        v-model="searchParams.categoryId"
        placeholder="商品分类"
        clearable
        @change="fetchProducts"
        class="category-select"
      >
        <el-option
          v-for="category in categories"
          :key="category.id"
          :label="category.name"
          :value="category.id"
        ></el-option>
      </el-select>
      <div class="nav-icons">
        <el-tooltip content="去购物车" placement="bottom">
          <el-button circle class="nav-icon-btn" @click="goToCart">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>
          </el-button>
        </el-tooltip>
        <el-tooltip content="地址管理" placement="bottom">
          <el-button circle class="nav-icon-btn" @click="goToAddress">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
          </el-button>
        </el-tooltip>
      </div>
    </div>

    <!-- 商品列表 -->
    <div v-loading="loading" class="product-grid">
      <div
        v-for="product in productPage.records"
        :key="product.id"
        class="product-card"
      >
        <div class="product-image" @click="toProductDetail(product.id)">
          <el-image
            :src="product.mainImage"
            alt="商品图片"
            fit="contain"
          ></el-image>
          <div v-if="product.stock <= 0" class="stock-overlay">
            <span>暂无库存</span>
          </div>
        </div>
        <div class="product-info">
          <h3 class="product-name" @click="toProductDetail(product.id)">{{ product.productName }}</h3>
          <div class="product-price">
            <span class="price">¥{{ product.price.toFixed(2) }}</span>
            <span v-if="product.stock > 0" class="stock-badge">有货</span>
            <span v-else class="stock-badge out">无库存</span>
          </div>
          <div class="product-actions">
            <el-button
              type="primary"
              size="small"
              @click="addToCart(product.id)"
              :disabled="product.stock <= 0"
              class="action-btn cart-btn"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>
              加入购物车
            </el-button>
            <el-button
              size="small"
              @click="sendAsGift(product.id, product.productName)"
              :disabled="product.stock <= 0"
              class="action-btn gift-btn"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="12" rx="2"/><path d="M5 8V6a2 2 0 012-2h10a2 2 0 012 2v2"/></svg>
              送礼物
            </el-button>
            <el-button
              size="small"
              @click="toProductDetail(product.id)"
              class="action-btn detail-btn"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              详情
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-if="productPage.records.length === 0 && !loading" class="empty-state">
      <el-empty description="暂无商品数据"></el-empty>
    </div>

    <!-- 分页 -->
    <div class="pagination-container" v-if="productPage.total > 0">
      <el-pagination
        :current-page="searchParams.pageNum"
        :page-size="searchParams.pageSize"
        :total="productPage.total"
        :page-sizes="[10, 20, 50]"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 送礼物对话框 -->
    <el-dialog
      v-model="giftDialogVisible"
      title="选择好友赠送礼物"
      width="500px"
      class="gift-dialog"
    >
      <div v-if="friendsList.length === 0">
        <el-empty description="暂无好友"></el-empty>
      </div>
      <div v-else>
        <div class="friend-list">
          <div
            v-for="friend in friendsList"
            :key="friend.friendId"
            class="friend-item"
            :class="{ selected: selectedFriendId === friend.friendId }"
            @click="selectedFriendId = friend.friendId"
          >
            <img :src="friend.avatar || defaultAvatar" class="friend-avatar" alt="">
            <div class="friend-info">
              <span class="friend-name">{{ friend.nickname }}</span>
              <span class="friend-status" v-if="friend.online">在线</span>
              <span class="friend-status offline" v-else>离线</span>
            </div>
          </div>
        </div>

        <div v-if="giftAccepted" style="margin-top: 15px; color: green;">
          ✅ 对方已接受礼物，可以去下单了
        </div>
      </div>

      <template #footer>
        <el-button @click="giftDialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          @click="confirmSendGift"
          :disabled="!selectedFriendId"
        >
          发送礼物请求
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import productApi from '@/api/product'
import cartApi from '@/api/cart'
import giftApi from '@/api/gift'
import orderApi from '@/api/order'
import friendApi from '@/api/friend'
import socket from '@/api/socket'

// 路由实例
const router = useRouter()

// 状态定义
const productPage = ref({
  records: [],
  total: 0,
  size: 10,
  current: 1
})
const categories = ref([])
const loading = ref(false)
const searchParams = ref({
  pageNum: 1,
  pageSize: 10,
  keyword: '',
  categoryId: null
})

// 送礼物相关状态
const giftDialogVisible = ref(false)
const selectedFriendId = ref('')
const friendsList = ref([])
const currentGiftProductId = ref(null)
const currentGiftProductName = ref(null)
const currentGiftRequestId = ref(null)
const orderSn = ref(null)
const giftAccepted = ref(false)
const currentUserId = ref(localStorage.getItem('userId') ? Number(localStorage.getItem('userId')) : null)
const username = ref(localStorage.getItem('username'))
const defaultAvatar = 'https://picsum.photos/id/1005/200'

// 获取商品列表
const fetchProducts = async () => {
  try {
    loading.value = true
    const res = await productApi.getProductPage(searchParams.value)

    if (res.data.code == 200) {
      const pageData = res.data.data || {}
      productPage.value = {
        ...pageData,
        records: pageData.records || []
      }
    } else {
      ElMessage.error(res.data?.msg || '获取商品列表失败')
      productPage.value.records = []
    }
  } catch (err) {
    console.error('获取商品列表失败:', err)
    ElMessage.error('获取商品列表失败，请稍后重试')
    productPage.value.records = []
  } finally {
    loading.value = false
  }
}

// 分页处理
const handleSizeChange = (size) => {
  searchParams.value.pageSize = size
  searchParams.value.pageNum = 1
  fetchProducts()
}

const handleCurrentChange = (page) => {
  searchParams.value.pageNum = page
  fetchProducts()
}

// 跳转到商品详情
const toProductDetail = (id) => {
  router.push(`/product/detail/${id}`)
}

// 加入购物车
const addToCart = async (productId) => {
  try {
    const userId = localStorage.getItem('userId')
    if (!userId) {
      ElMessage.warning('请先登录')
      router.push('/login')
      return
    }

    const res = await cartApi.addToCart({
      userId: Number(userId),
      productId,
      quantity: 1
    }, {
      params: { userId: Number(userId) }
    })

    if (res.data?.code == 200) {
      ElMessageBox.confirm(
        '商品已加入购物车',
        '操作成功',
        {
          confirmButtonText: '去购物车',
          cancelButtonText: '继续购物',
          type: 'success',
          distinguishCancelAndClose: true
        }
      ).then(() => {
        router.push('/cart')
      }).catch(action => {
        if (action === 'cancel') {
          // 继续购物
        }
      })
    } else {
      ElMessage.error(res.data?.msg || '加入购物车失败')
    }
  } catch (err) {
    console.error('加入购物车失败:', err)
    ElMessage.error('加入购物车失败')
  }
}

// 送礼物相关方法
const fetchFriends = async () => {
  try {
    const res = await friendApi.getFriendList(currentUserId.value)
    if (res.data.success) {
      friendsList.value = res.data.data
    }
  } catch (err) {
    console.error('获取好友列表失败:', err)
    ElMessage.error('获取好友列表失败')
  }
}

const sendAsGift = (productId, productName) => {
  const userId = localStorage.getItem('userId')
  if (!userId) {
    ElMessage.warning('请先登录')
    router.push('/login')
    return
  }

  currentGiftProductId.value = productId
  currentGiftProductName.value = productName
  fetchFriends()
  giftDialogVisible.value = true
  giftAccepted.value = false
  currentGiftRequestId.value = null
}

const confirmSendGift = async () => {
  if (!selectedFriendId.value) {
    ElMessage.warning('请选择好友')
    return
  }

  // 检查WebSocket连接状态
  if (!socket.isConnected) {
    ElMessage.warning('WebSocket连接未建立，请稍后重试')
    return
  }

  try {
    await createGiftOrder(currentGiftProductId.value, selectedFriendId.value)
    console.log(orderSn.value)
    if (orderSn.value) {
      const createRes = await giftApi.sendGiftRequest({
        senderUserId: Number(currentUserId.value),
        receiverUserId: selectedFriendId.value,
        productId: currentGiftProductId.value,
        productName: currentGiftProductName.value,
        orderSn: orderSn.value
      })
      if (createRes.data.code == 200) {
        currentGiftRequestId.value = createRes.data.data

        // 通过WebSocket发送礼物请求通知给接收者
        const socketResponse = await socket.send({
          type: '5',//礼物消息
          senderId: Number(currentUserId.value),
          receiverId: selectedFriendId.value,
          productId: currentGiftProductId.value,
          productName: currentGiftProductName.value,
          requestId: currentGiftRequestId.value,
          content: '接到对方向您发送礼物的请求',
          sendTime: new Date().toISOString(),
        })
        console.log(socketResponse)

        ElMessage.success('创建订单成功，礼物请求已发送给对方，请提醒对方接受礼物...')
        giftDialogVisible.value = false
      }
    }
  } catch (err) {
    ElMessage.error('发送礼物请求失败，请稍后重试')
  }
}

// 创建礼物订单
const createGiftOrder = async (productId, receiverUserId) => {
  try {
    const userId = localStorage.getItem('userId')
    if (!userId) {
      ElMessage.warning('请先登录')
      router.push('/login')
      return
    }

    // 获取商品信息
    const productRes = await productApi.getProductById(productId)
    const product = productRes.data.data

    // 直接创建订单，无需地址信息，添加isGift标记
    const res = await orderApi.createOrder({
      userId: Number(userId),
      items: [{
        productId: product.id,
        count: 1,
        price: product.price
      }],
      isGift: true,
      receiverUserId: receiverUserId,
      isSeckill: 0,
      username: username.value,
      giftRequestId: Number(currentGiftRequestId.value) || 0
    })
    console.log("创建礼物订单返回"+res)

    if (res.data && res.data.code == 200) {
      orderSn.value = res.data.data
      ElMessage.success('礼物订单创建成功')
      router.push(`/order/detail/${orderSn.value}`)
    } else {
      ElMessage.error(res.data?.msg || '创建礼物订单失败')
    }
  } catch (err) {
    console.error('创建礼物订单失败:', err)
    ElMessage.error('创建礼物订单失败，请稍后重试')
  }
}

// 跳转到购物车
const goToCart = () => {
  router.push('/cart')
}

// 跳转到地址管理（新增）
const goToAddress = () => {
  router.push('/address')
}

// 页面加载时初始化
onMounted(() => {
  fetchProducts()

  // 初始化WebSocket连接
  if (currentUserId.value) {
    socket.connect({ userId: currentUserId.value })
  }
})

// 页面卸载时清理
onUnmounted(() => {
  // 移除所有事件监听
  socket.off('open')
  socket.off('close')
  socket.off('message')
  socket.off('error')
})

// 监听userId变化，当用户登录状态改变时重新连接
watch(currentUserId, (newUserId) => {
  if (newUserId) {
    socket.connect({ userId: newUserId })
  } else {
    socket.close()
  }
})
</script>

<style scoped>
.page-container {
  max-width: 1400px;
  margin: 0 auto;
}

/* Search section */
.search-section {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.search-input {
  flex: 1;
  min-width: 200px;
}

.search-input :deep(.el-input__wrapper) {
  border-radius: var(--app-radius-lg);
  box-shadow: 0 0 0 1px var(--app-border-light) inset;
}

.search-input :deep(.el-input-group__append) {
  border-radius: 0 var(--app-radius-lg) var(--app-radius-lg) 0;
  overflow: hidden;
}

.search-input :deep(.el-input-group__append .el-button) {
  padding: 8px 16px;
}

.category-select {
  width: 180px;
}

.nav-icons {
  display: flex;
  gap: 8px;
}

.nav-icon-btn {
  width: 40px !important;
  height: 40px !important;
  padding: 0 !important;
  background: var(--app-bg-card) !important;
  border: 1px solid var(--app-border-light) !important;
  border-radius: var(--app-radius-md) !important;
  color: var(--app-text-secondary) !important;
  transition: all 0.25s ease !important;
}

.nav-icon-btn:hover {
  border-color: var(--app-color-primary) !important;
  color: var(--app-color-primary) !important;
  background: var(--app-color-primary-soft) !important;
}

/* Product grid */
.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.product-card {
  background: var(--app-bg-card);
  border-radius: var(--app-radius-xl);
  border: 1px solid var(--app-border-subtle);
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: var(--app-shadow-card);
}

.product-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--app-shadow-card-hover);
  border-color: rgba(255, 126, 126, 0.15);
}

.product-image {
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--app-bg-soft);
  overflow: hidden;
  cursor: pointer;
  position: relative;
}

.product-image :deep(.el-image) {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.product-card:hover .product-image :deep(.el-image) {
  transform: scale(1.05);
}

.stock-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  backdrop-filter: blur(2px);
}

.product-info {
  padding: 16px;
}

.product-name {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--app-text-primary);
  margin: 0 0 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  transition: color 0.2s;
}

.product-name:hover {
  color: var(--app-color-primary);
}

.product-price {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.price {
  color: var(--app-color-primary);
  font-weight: 700;
  font-size: 1.125rem;
}

.stock-badge {
  font-size: 0.75rem;
  padding: 1px 8px;
  border-radius: var(--app-radius-full);
  background: rgba(46, 204, 113, 0.12);
  color: #27ae60;
}

.stock-badge.out {
  background: rgba(155, 155, 155, 0.1);
  color: var(--app-text-muted);
}

.product-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.action-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 0.8125rem !important;
  border-radius: var(--app-radius-md) !important;
  transition: all 0.25s ease !important;
}

.action-btn.cart-btn {
  background: var(--app-color-primary) !important;
  border-color: var(--app-color-primary) !important;
}

.action-btn.cart-btn:hover:not(:disabled) {
  background: var(--app-color-primary-dark) !important;
  transform: translateY(-1px);
}

.action-btn.gift-btn {
  border-color: var(--app-border-light) !important;
  color: var(--app-text-secondary) !important;
}

.action-btn.gift-btn:hover:not(:disabled) {
  border-color: var(--app-color-primary) !important;
  color: var(--app-color-primary) !important;
}

.action-btn.detail-btn {
  background: transparent !important;
  border-color: var(--app-border-light) !important;
  color: var(--app-text-secondary) !important;
}

.action-btn.detail-btn:hover {
  border-color: var(--app-color-primary) !important;
  color: var(--app-color-primary) !important;
}

.empty-state {
  margin: 60px 0;
  text-align: center;
}

.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 28px;
  padding: 20px 0;
}

/* 好友选择列表（送礼对话框） */
.friend-list {
  max-height: 300px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.friend-list::-webkit-scrollbar {
  width: 4px;
}

.friend-list::-webkit-scrollbar-thumb {
  background: var(--app-border-light);
  border-radius: 2px;
}

.friend-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: var(--app-radius-md);
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
}

.friend-item:hover {
  background: var(--app-color-primary-muted);
  border-color: var(--app-border-light);
}

.friend-item.selected {
  background: var(--app-color-primary-soft);
  border-color: var(--app-color-primary-light);
}

.friend-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.friend-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.friend-name {
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--app-text-primary);
}

.friend-status {
  font-size: 0.75rem;
  color: #2ecc71;
}

.friend-status.offline {
  color: var(--app-text-muted);
}

@media (max-width: 768px) {
  .product-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .product-actions {
    flex-direction: column;
  }

  .search-section {
    flex-direction: column;
  }

  .search-input, .category-select {
    width: 100%;
  }

  .nav-icons {
    width: 100%;
    justify-content: flex-end;
  }
}

@media (max-width: 480px) {
  .product-grid {
    grid-template-columns: 1fr;
  }
}
</style>
